'use client';

import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import type { SqlQueryExecution } from '@repo/api-gw-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { AiStarSmall } from '@/components/assistant-ui/assets/aiStarSmall';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';

import { useSqlQueryAnalyze } from './useSqlQueryAnalyze';

const POLL_INTERVAL_MS = 1000;

type Phase = 'idle' | 'executing' | 'analyzing' | 'complete' | 'error';

interface QueryExecTracker {
  id: string;
  label: string;
  status: 'running' | 'succeeded' | 'failed';
}

const isTerminalStatus = (status: string): boolean =>
  status === 'SUCCEEDED' || status === 'FAILED' || status === 'CANCELLED';

interface SqlQueryAnalyzerPanelProps {
  workspaceId: string;
  getQuery: () => string;
  selectedEngineId: string;
  onClose: () => void;
}

export const SqlQueryAnalyzerPanel = ({
  workspaceId,
  getQuery,
  selectedEngineId,
  onClose,
}: SqlQueryAnalyzerPanelProps) => {
  const dal = useDAL();
  const dalRef = useRef(dal);
  useEffect(() => {
    dalRef.current = dal;
  }, [dal]);

  const [analyzeMode, setAnalyzeMode] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [executions, setExecutions] = useState<QueryExecTracker[]>([]);
  const [phaseError, setPhaseError] = useState<string | null>(null);

  const pollIntervalsRef = useRef<NodeJS.Timeout[]>([]);
  const {
    markdown,
    isStreaming,
    isComplete: analysisComplete,
    error: analysisError,
    analyze,
    reset: resetAnalysis,
  } = useSqlQueryAnalyze(workspaceId);

  // Cleanup polls on unmount
  useEffect(() => {
    return () => {
      pollIntervalsRef.current.forEach(clearInterval);
    };
  }, []);

  // Derive final phase from analysisComplete instead of using setState in effect
  const derivedPhase = useMemo((): Phase => {
    if (phase !== 'analyzing') return phase;
    if (!analysisComplete || isStreaming) return 'analyzing';
    return analysisError ? 'error' : 'complete';
  }, [phase, analysisComplete, isStreaming, analysisError]);

  const derivedError =
    derivedPhase === 'error' ? phaseError || analysisError : phaseError;

  const buildQueries = useCallback((): { label: string; sql: string }[] => {
    const trimmed = getQuery().trim();
    if (analyzeMode) {
      return [
        {
          label: 'DISTRIBUTED',
          sql: `EXPLAIN (TYPE DISTRIBUTED, FORMAT TEXT) ${trimmed}`,
        },
        { label: 'EXPLAIN ANALYZE', sql: `EXPLAIN ANALYZE ${trimmed}` },
      ];
    }
    return [
      { label: 'VALIDATE', sql: `EXPLAIN (TYPE VALIDATE) ${trimmed}` },
      {
        label: 'DISTRIBUTED',
        sql: `EXPLAIN (TYPE DISTRIBUTED, FORMAT TEXT) ${trimmed}`,
      },
      { label: 'IO', sql: `EXPLAIN (TYPE IO, FORMAT JSON) ${trimmed}` },
    ];
  }, [getQuery, analyzeMode]);

  const handleExplain = useCallback(async () => {
    // Reset state
    setPhaseError(null);
    resetAnalysis();
    pollIntervalsRef.current.forEach(clearInterval);
    pollIntervalsRef.current = [];

    const queries = buildQueries();
    setPhase('executing');

    const trackers: QueryExecTracker[] = queries.map((q) => ({
      id: '',
      label: q.label,
      status: 'running',
    }));
    setExecutions(trackers);

    try {
      // Submit all queries concurrently
      const startedExecutions = await Promise.all(
        queries.map((q) =>
          dalRef.current.lakehouse.startSqlQueryExecution(workspaceId, {
            sqlEngineId: selectedEngineId,
            query: q.sql,
          })
        )
      );

      // Update trackers with real IDs
      const updatedTrackers = startedExecutions.map(
        (exec: SqlQueryExecution, i: number) => ({
          id: exec.id,
          label: queries[i].label,
          status: isTerminalStatus(exec.status)
            ? exec.status === 'SUCCEEDED'
              ? ('succeeded' as const)
              : ('failed' as const)
            : ('running' as const),
        })
      );
      setExecutions(updatedTrackers);

      // Poll each non-terminal execution
      const completionPromises = updatedTrackers.map(
        (tracker, i) =>
          new Promise<{ id: string; succeeded: boolean }>((resolve) => {
            if (tracker.status !== 'running') {
              resolve({
                id: tracker.id,
                succeeded: tracker.status === 'succeeded',
              });
              return;
            }

            const interval = setInterval(() => {
              void (async () => {
                try {
                  const execution =
                    await dalRef.current.lakehouse.getSqlQueryExecution(
                      workspaceId,
                      startedExecutions[i].id
                    );

                  if (isTerminalStatus(execution.status)) {
                    clearInterval(interval);
                    const succeeded = execution.status === 'SUCCEEDED';
                    setExecutions((prev) =>
                      prev.map((t, idx) =>
                        idx === i
                          ? { ...t, status: succeeded ? 'succeeded' : 'failed' }
                          : t
                      )
                    );
                    resolve({ id: execution.id, succeeded });
                  }
                } catch {
                  clearInterval(interval);
                  setExecutions((prev) =>
                    prev.map((t, idx) =>
                      idx === i ? { ...t, status: 'failed' } : t
                    )
                  );
                  resolve({ id: startedExecutions[i].id, succeeded: false });
                }
              })();
            }, POLL_INTERVAL_MS);

            pollIntervalsRef.current.push(interval);
          })
      );

      const results = await Promise.all(completionPromises);
      const succeededIds = results.filter((r) => r.succeeded).map((r) => r.id);

      if (succeededIds.length === 0) {
        setPhase('error');
        setPhaseError('All EXPLAIN queries failed');
        return;
      }

      // Phase 2: Stream AI analysis
      setPhase('analyzing');
      analyze(succeededIds);
    } catch (err) {
      setPhase('error');
      setPhaseError(
        err instanceof Error ? err.message : 'Failed to start queries'
      );
    }
  }, [buildQueries, workspaceId, selectedEngineId, resetAnalysis, analyze]);

  const handleRetry = useCallback(() => {
    setPhase('idle');
    setPhaseError(null);
    resetAnalysis();
    setExecutions([]);
  }, [resetAnalysis]);

  return (
    <PanelWrapper
      header={{
        title: 'AI Query Analysis',
        onClose,
      }}
    >
      <Stack gap='16px' sx={{ p: '24px' }}>
        {/* Idle state */}
        {derivedPhase === 'idle' && (
          <>
            <Stack direction='row' alignItems='center' gap='12px'>
              <ButtonWithIcon
                variant='outlined'
                text='Explain with AI'
                icon={<AiStarSmall />}
                onClick={() => void handleExplain()}
                disabled={!getQuery().trim()}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={analyzeMode}
                    onChange={(e) => setAnalyzeMode(e.target.checked)}
                  />
                }
                label={
                  <Typography variant='body2' color='text.secondary'>
                    Analyze (Run this query)
                  </Typography>
                }
              />
            </Stack>
          </>
        )}

        {/* Executing state — progress chips */}
        {(derivedPhase === 'executing' ||
          derivedPhase === 'analyzing' ||
          derivedPhase === 'complete' ||
          (derivedPhase === 'error' && executions.length > 0)) && (
          <Stack gap='12px'>
            <Stack direction='row' alignItems='center' gap='8px'>
              <Typography variant='subtitle2' color='text.secondary'>
                {derivedPhase === 'executing'
                  ? 'Running queries...'
                  : 'Queries'}
              </Typography>
              {derivedPhase === 'executing' && <CircularProgress size={14} />}
            </Stack>
            <Stack direction='row' flexWrap='wrap' gap='6px'>
              {executions.map((exec) => (
                <Chip
                  key={exec.label}
                  size='small'
                  label={exec.label}
                  icon={
                    exec.status === 'running' ? (
                      <CircularProgress size={12} />
                    ) : exec.status === 'succeeded' ? (
                      <i className='material-symbols-check-circle-outline-rounded h-[16px] w-[16px]' />
                    ) : (
                      <i className='material-symbols-error-outline-rounded h-[16px] w-[16px]' />
                    )
                  }
                  variant='outlined'
                  sx={{
                    '& .MuiChip-icon': {
                      color:
                        exec.status === 'succeeded'
                          ? 'var(--mui-palette-success-main)'
                          : exec.status === 'failed'
                            ? 'var(--mui-palette-error-main)'
                            : undefined,
                    },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {/* Analyzing state */}
        {derivedPhase === 'analyzing' && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography variant='subtitle2' color='text.secondary'>
              Analyzing...
            </Typography>
            <CircularProgress size={14} />
          </Stack>
        )}

        {/* Streaming / Complete markdown */}
        {markdown && (
          <Box
            sx={{
              '& h2': {
                fontSize: '0.95rem',
                fontWeight: 600,
                mt: '16px',
                mb: '8px',
                '&:first-of-type': { mt: 0 },
              },
              '& h3': {
                fontSize: '0.875rem',
                fontWeight: 600,
                mt: '12px',
                mb: '4px',
              },
              '& p': {
                fontSize: '0.875rem',
                lineHeight: 1.6,
                mb: '8px',
              },
              '& ul, & ol': {
                fontSize: '0.875rem',
                pl: '20px',
                mb: '8px',
              },
              '& li': {
                mb: '4px',
              },
              '& code': {
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                backgroundColor: 'var(--mui-palette-action-hover)',
                borderRadius: '4px',
                px: '4px',
              },
              '& pre': {
                backgroundColor: 'var(--mui-palette-action-hover)',
                borderRadius: '8px',
                p: '12px',
                overflow: 'auto',
                mb: '8px',
              },
            }}
          >
            <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
          </Box>
        )}

        {/* Error state */}
        {derivedPhase === 'error' && derivedError && (
          <Alert severity='error' variant='outlined'>
            {derivedError}
          </Alert>
        )}

        {/* Complete / Error — action buttons */}
        {(derivedPhase === 'complete' || derivedPhase === 'error') && (
          <Stack alignItems='flex-start'>
            <ButtonWithIcon
              variant='outlined'
              size='small'
              text={derivedPhase === 'error' ? 'Retry' : 'Re-analyze'}
              icon='material-symbols-refresh-rounded'
              onClick={
                derivedPhase === 'error'
                  ? handleRetry
                  : () => void handleExplain()
              }
            />
          </Stack>
        )}
      </Stack>
    </PanelWrapper>
  );
};
